import { Core, Decorative } from "@faire/design-tokens";
import { BrandProductSpotlight } from "@faire/retailer-visitor-shared/components/BrandProductSpotlight/index";
import { getProductSpotlightCards } from "@faire/retailer-visitor-shared/components/BrandProductSpotlight/utils";
import { useNationality } from "@faire/retailer-visitor-shared/hooks/retailer/useNationality";
import { getSettingUpdatedNAMCommissionAnnouncement } from "@faire/retailer-visitor-shared/settings/getSettingUpdatedNAMCommissionAnnouncement";
import {
  ACCORDION_ITEMS,
  commissionUpdateNAMAccordionItems,
} from "@faire/retailer-visitor-shared/views/brands/landing-page/BrandsLandingPageFAQs";
import { BrandsLandingPageNews } from "@faire/retailer-visitor-shared/views/brands/landing-page/BrandsLandingPageNews";
import {
  StyledSellOnFaireButton,
  CTAPosition,
} from "@faire/retailer-visitor-shared/views/brands/landing-page/const";
import { QuestionAccordion } from "@faire/retailer-visitor-shared/views/brands/landing-page/QuestionAccordion";
import { useStrictLocalization } from "@faire/web/common/localization/localize";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { optimizeImageUrl } from "@faire/web/common/transform-images";
import { Accordion } from "@faire/web/slate/Accordion";
import { Button } from "@faire/web/slate/Button";
import { Column, Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { WhenVisibilityIsImminent } from "@faire/web/ui/VisibilitySensor/WhenVisibilityIsImminent";
import { trackBrandLeadsApplyToSellOnFaireClick } from "@faire/web-api--source/events/brandLeads/click/applyToSellOnFaire";
import { trackBrandLeadsBrandsPageQandaClick } from "@faire/web-api--source/events/brandLeads/click/brandsPageQanda";
import { trackBrandLeadsScheduleACallClick } from "@faire/web-api--source/events/brandLeads/click/scheduleACall";
import { trackBrandLeadsBrandPageBrandsQnaSectionView } from "@faire/web-api--source/events/brandLeads/view/brandPageBrandsQnaSection";
import { trackBrandLeadsBrandsPageBottomSectionView } from "@faire/web-api--source/events/brandLeads/view/brandsPageBottomSection";
import { trackBrandLeadsBrandsPageSpotlightCarouselView } from "@faire/web-api--source/events/brandLeads/view/brandsPageSpotlightCarousel";
import { trackBrandLeadsNewsSectionView } from "@faire/web-api--source/events/brandLeads/view/newsSection";
import React, { useCallback, useState } from "react";
import styled, { css } from "styled-components";

interface IProp {
  onButtonClick: () => void;
  calendlyLink?: string;
  isPersonalized?: boolean;
}

export const BrandsLandingPageBottomSection: React.FC<IProp> = ({
  onButtonClick,
  calendlyLink,
  isPersonalized,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const { isCanadian, isAmerican } = useNationality();
  const isNorthAmerican = isCanadian || isAmerican;
  const viewport = useViewport();
  const { isEurope: isShowingCaseStudies } = useNationality();

  const [openIndex, setOpenIndex] = useState<number | undefined>(
    viewport.isMobile ? undefined : 0
  );

  const actionWhenSpotlightVisibleHandler = useCallback(() => {
    const key = getProductSpotlightCards(strictLocalize, isNorthAmerican)[0]
      ?.eventKey;
    if (key) {
      trackBrandLeadsBrandsPageSpotlightCarouselView(key);
    }
  }, [strictLocalize, isNorthAmerican]);

  const toggleAccordionItem = (index: number) => {
    if (openIndex === index) {
      setOpenIndex(undefined);
    } else {
      const eventKey = getSettingUpdatedNAMCommissionAnnouncement()
        ? commissionUpdateNAMAccordionItems(strictLocalize)[index]?.eventKey
        : ACCORDION_ITEMS(strictLocalize)[index]?.eventKey;
      if (eventKey) {
        trackBrandLeadsBrandsPageQandaClick(eventKey);
      }
      setOpenIndex(index);
    }
  };

  const bottomBannerViewRef = useDoActionWhenVisible(
    trackBrandLeadsBrandsPageBottomSectionView
  );

  return (
    <WhenVisibilityIsImminent>
      <Column style={{ width: "100%" }}>
        <BrandProductSpotlight
          onView={actionWhenSpotlightVisibleHandler}
          isPersonalized={isPersonalized}
          showCaseStudies={isShowingCaseStudies}
        />
        <FAQWrapper $showCaseStudies={isShowingCaseStudies}>
          <Row style={{ width: "100%", justifyContent: "center" }}>
            <Column
              style={{ width: "700px" }}
              justify="center"
              align={viewport.isMobile ? "flex-start" : "center"}
            >
              {getSettingUpdatedNAMCommissionAnnouncement() ? (
                <>
                  <Spacer size={viewport.isMobile ? "3x" : "9x"} />
                  <Accordion
                    items={commissionUpdateNAMAccordionItems(strictLocalize)}
                  />
                  <Spacer size={viewport.isMobile ? 0 : "10x"} />
                </>
              ) : (
                <>
                  <Spacer size={viewport.isMobile ? "4x" : 88} />
                  <Header
                    variant={{
                      mobileAndAbove: "pageHeaderSerifRegular",
                      tabletAndAbove: "displayMSerifRegular",
                      desktopAndAbove: "displayLSerifRegular",
                    }}
                  >
                    <StrictLocalMsg defaultMessage="You may be wondering..." />
                  </Header>
                  <Spacer size={viewport.isMobile ? "1x" : "6x"} />
                  <Row justify="center">
                    <QuestionAccordion
                      onView={trackBrandLeadsBrandPageBrandsQnaSectionView}
                      information={ACCORDION_ITEMS(strictLocalize)}
                      openIndex={openIndex}
                      onItemClick={toggleAccordionItem}
                    />
                  </Row>
                  <Spacer size={viewport.isMobile ? "2x" : "10x"} />
                </>
              )}
            </Column>
          </Row>
        </FAQWrapper>
      </Column>
      <BrandsLandingPageNews onView={trackBrandLeadsNewsSectionView} />
      <div ref={bottomBannerViewRef}>
        {viewport.isMobile ? (
          <BottomBannerMobile
            onButtonClick={onButtonClick}
            isPersonalized={isPersonalized}
            calendlyLink={calendlyLink}
          />
        ) : (
          <BottomBannerDesktop
            onButtonClick={onButtonClick}
            isPersonalized={isPersonalized}
            calendlyLink={calendlyLink}
          />
        )}
      </div>
    </WhenVisibilityIsImminent>
  );
};

const BottomBannerDesktop: React.FC<{
  onButtonClick: () => void;
  calendlyLink?: string;
  isPersonalized?: boolean;
}> = ({ onButtonClick, calendlyLink, isPersonalized }) => {
  const viewport = useViewport();
  const isSSR = useIsSSR();

  return (
    <Column style={{ width: "100%", position: "relative" }} justify="center">
      <BackgroundImage
        src={optimizeImageUrl(
          {
            url: "https://cdn.faire.com/fastly/5f577365c2721dea3f28c5857027c1637a2a9971c5a7a431acdedae0951b2255.png",
          },
          isSSR,
          {
            width: 2560,
          }
        )}
      />
      <Row style={{ width: "100%" }} justify="center">
        <StyledTypography
          variant={
            viewport.isDesktopAndAbove
              ? "displayLSerifRegular"
              : "displayMSerifRegular"
          }
        >
          <StrictLocalMsg defaultMessage="Ready to grow your wholesale business?" />
        </StyledTypography>
      </Row>
      <Row style={{ width: "100%" }} justify="center">
        {isPersonalized && calendlyLink ? (
          <StyledSellOnFaireButton
            linkTo={calendlyLink}
            target="_blank"
            onClick={() => {
              trackBrandLeadsScheduleACallClick(CTAPosition.BOTTOM_SECTION);
            }}
            style={{
              position: "absolute",
              top: viewport.isTablet ? 180 : 300,
            }}
          >
            <StrictLocalMsg defaultMessage="Schedule a Call" />
          </StyledSellOnFaireButton>
        ) : (
          <StyledSellOnFaireButton
            onClick={() => {
              trackBrandLeadsApplyToSellOnFaireClick(
                CTAPosition.BOTTOM_SECTION
              );
              onButtonClick();
            }}
            style={{
              position: "absolute",
              top: viewport.isTablet ? 180 : 300,
            }}
          >
            <StrictLocalMsg
              defaultMessage="Get started"
              description={{
                text: "Call to action to start signing up and sell on Faire",
              }}
            />
          </StyledSellOnFaireButton>
        )}
      </Row>
    </Column>
  );
};

const BottomBannerMobile: React.FC<{
  onButtonClick: () => void;
  calendlyLink?: string;
  isPersonalized?: boolean;
}> = ({ onButtonClick, calendlyLink, isPersonalized }) => {
  return (
    <Column
      style={{
        width: "100%",
        backgroundColor: Decorative.shade.yellow,
        padding: getSpacing("4x"),
      }}
    >
      <Typography
        color={Core.text.inverse}
        variant="displaySSerifRegular"
        align="left"
      >
        <StrictLocalMsg defaultMessage="Ready to see your products in stores around the world?" />
      </Typography>

      <Spacer size="4x" />
      <Row style={{ width: "100%" }} justify="center">
        {isPersonalized && calendlyLink ? (
          <Button
            variant="secondary"
            linkTo={calendlyLink}
            target="_blank"
            onClick={() => {
              trackBrandLeadsScheduleACallClick(CTAPosition.BOTTOM_SECTION);
            }}
            style={{
              width: "100%",
            }}
          >
            <StrictLocalMsg defaultMessage="Schedule a Call" />
          </Button>
        ) : (
          <Button
            variant="secondary"
            style={{ width: "100%" }}
            onClick={() => {
              trackBrandLeadsApplyToSellOnFaireClick(
                CTAPosition.BOTTOM_SECTION
              );
              onButtonClick();
            }}
          >
            <StrictLocalMsg
              defaultMessage="Get Started"
              description={{
                text: "Call to action to start signing up and sell on Faire",
              }}
            />
          </Button>
        )}
      </Row>
    </Column>
  );
};

const Header = styled(Typography)`
  &&& {
    ${responsiveMediaQueries.mobile} {
      margin-left: ${getSpacing("3x")};
    }
  }
`;

const BackgroundImage = styled.img`
  width: 100%;

  ${responsiveMediaQueries.desktopAndAbove} {
    height: 430px;
  }

  ${responsiveMediaQueries.tablet} {
    height: 260px;
  }

  ${responsiveMediaQueries.mobile} {
    height: 300px;
  }
`;

const StyledTypography = styled(Typography).attrs({
  align: "center",
  color: "white",
})`
  ${responsiveMediaQueries.tablet} {
    top: 65px;
    position: absolute;
  }

  ${responsiveMediaQueries.desktopAndAbove} {
    top: 110px;
    position: absolute;
  }
`;

const FAQWrapper = styled(Column).attrs({ justify: "center" })<{
  $showCaseStudies?: boolean;
}>`
  width: 100%;

  ${responsiveMediaQueries.tabletAndAbove} {
    ${({ $showCaseStudies }) =>
      $showCaseStudies
        ? css`
            background-color: ${Decorative.tint.neutral};
          `
        : null}
  }
`;
